import * as React from 'react'

import {
  Box,
  Checkbox,
  Table,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import { OverflowContextProvider } from 'modules/core/lib/overflowContext'
import { employeeUser } from 'modules/core/store/collections/users'

import { employeeReferenceContract } from 'modules/contracts/lib/utils'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { EmployeeAvatar } from 'modules/employees/components/EmployeeAvatar'
import { normalizeEmployee } from 'modules/employees/components/EmployeeAvatar/normalize'
import type Employee from 'modules/employees/store/models/Employee'
import { employeeJob } from 'modules/job_catalog/store/collections/roles'

import { CreatePermissionContext } from '../../CreatePermissionContext'
import ActionButtons from './_ActionButtons'
import type { Status } from './_Indicators'
import Indicators from './_Indicators'
import ItemWrapper from './_wrapper'

interface Props {
  employee: Employee
  selectedEmployeesIds: number[]
  onSelectEmployee: (employee: Employee, value: boolean) => void
  canBulkEdit: boolean
  position: number
  contractVersions: ContractVersions
}

const useMemoHiredOn = createComputed((employee: Employee, contractVersions: ContractVersions) => {
  const referenceContractDate = employeeReferenceContract(
    contractVersions,
    employee
  )?.startsOn?.fromNow()

  if (employee.seniorityDate === '') return referenceContractDate
  return employee.seniorityDate?.fromNow() || referenceContractDate || '-'
})

const useSelected = createComputed((employee: Employee, selectedEmployeesIds: number[]) => {
  return !!selectedEmployeesIds?.includes(employee.attributes.id)
})

const Item = ({
  employee,
  selectedEmployeesIds,
  onSelectEmployee,
  position,
  canBulkEdit,
  contractVersions,
}: Props) => {
  const hiredOn = useMemoHiredOn(employee, contractVersions)
  const lastNameRef = React.useRef(null)
  const jobRef = React.useRef(null)
  const selected = useSelected(employee, selectedEmployeesIds).get()
  const job = employeeJob(contractVersions, employee) || '-'
  const canCreateEmployee = React.useContext(CreatePermissionContext).authorization
  const [open, toggle] = useToggle(false)
  const [openLastname, toggleLastname] = useToggle(false)

  const onSelect = () => {
    onSelectEmployee(employee, !selected)
  }

  const employeeStatus = (): Status => {
    const today = moment()
    const startDate = employeeReferenceContract(contractVersions, employee)?.startsOn

    if (employee.isTerminated) return 'terminated'

    const { invited, confirmed } = employeeUser(employee).attributes

    if (!invited) return 'notInvited'

    if (confirmed && startDate && startDate > today) return 'accepted'

    if (confirmed) return 'active'

    if (invited) return 'invited'

    return 'notInvited'
  }

  const bulkEditProps: React.ComponentProps<typeof Box> = canBulkEdit
    ? {
        height: 'full',
        width: 'full',
        paddingX: 's16',
        paddingY: 's16',
        gap: 's16',
        onClick: onSelect,
      }
    : {}

  const AvatarCell = () => {
    return (
      <Table.Cell rowClickable={!canBulkEdit}>
        <Box flexDirection="row" alignItems="center" {...bulkEditProps}>
          {canBulkEdit && (
            <Checkbox
              checked={selected}
              ariaLabel={employeeUser(employee).fullName()}
              onChange={() => {}}
            />
          )}
          <EmployeeAvatar
            linkProfile
            employee={normalizeEmployee(employee)}
            variant="large"
            usePreferredName
          />
        </Box>
      </Table.Cell>
    )
  }

  const assertCellEllipsis = (text: string, maxCharacters = 8): boolean => {
    return text.length > maxCharacters
  }

  const showTooltip = (
    toggle: (value: boolean) => () => void,
    open: boolean,
    text: string,
    ref: {
      current: null | HTMLElement
    }
  ) => {
    return text.length > 15 ? (
      <OverflowContextProvider childrenRef={ref} value={text}>
        <Tooltip.Alt
          attachment="bottom center"
          targetAttachment="top center"
          toggle={toggle}
          open={open}
          label={text}
        >
          <Text.Normal
            weight="medium"
            color="grey1000"
            whiteSpace="nowrap"
            innerRef={ref}
            ellipsis={assertCellEllipsis(text || '')}
          >
            {text}
          </Text.Normal>
        </Tooltip.Alt>
      </OverflowContextProvider>
    ) : (
      <OverflowContextProvider childrenRef={ref} value={text}>
        <Text.Normal
          weight="medium"
          color="grey1000"
          whiteSpace="nowrap"
          innerRef={ref}
          ellipsis={assertCellEllipsis(text || '')}
        >
          {text}
        </Text.Normal>
      </OverflowContextProvider>
    )
  }

  return (
    <ItemWrapper employee={employee}>
      <AvatarCell />
      <Table.Cell>
        <Text.Normal
          weight="medium"
          color="grey1000"
          whiteSpace="nowrap"
          ellipsis={assertCellEllipsis(employeeUser(employee).preferredName || '')}
          className="notranslate"
        >
          {employeeUser(employee).preferredName}
        </Text.Normal>
      </Table.Cell>
      <Table.Cell>
        {showTooltip(
          toggleLastname,
          openLastname,
          employeeUser(employee).lastName || '',
          lastNameRef
        )}
      </Table.Cell>
      <Table.Cell>{showTooltip(toggle, open, job, jobRef)}</Table.Cell>
      <Table.Cell>
        <Text.Normal
          color="grey800"
          whiteSpace="nowrap"
          ellipsis={assertCellEllipsis(hiredOn.get() ?? '')}
        >
          {hiredOn.get() ?? ''}
        </Text.Normal>
      </Table.Cell>
      {canCreateEmployee && (
        <Table.Cell>
          <Box flexDirection="row">
            <Indicators employeeStatus={employeeStatus()} />
          </Box>
        </Table.Cell>
      )}
      <Table.Cell rowClickable={!canCreateEmployee}>
        <ActionButtons isFirst={position === 0} employee={employee} status={employeeStatus()} />
      </Table.Cell>
    </ItemWrapper>
  )
}

export default observer(Item)
